import { get, authGet, authPost, authPut, authDelete } from './apiService';

/**
 * Repository service for handling repository-related API calls
 */
export const repositoryService = {
  /**
   * Get all repositories
   * @returns {Promise<Array>} Array of repositories
   */
  async getRepositories() {
    try {
      return await get('/api/repositories');
    } catch (error) {
      console.error('Error fetching repositories:', error);
      throw error;
    }
  },

  /**
   * Create a new repository
   * @param {Object} repositoryData - Repository data
   * @returns {Promise<Object>} Created repository
   */
  async createRepository(repositoryData) {
    try {
      return await authPost('/api/repositories', repositoryData);
    } catch (error) {
      console.error('Error creating repository:', error);
      throw error;
    }
  },

  /**
   * Update an existing repository
   * @param {string} repositoryId - Repository ID
   * @param {Object} repositoryData - Updated repository data
   * @returns {Promise<Object>} Updated repository
   */
  async updateRepository(repositoryId, repositoryData) {
    try {
      return await authPut(`/api/repositories/${repositoryId}`, repositoryData);
    } catch (error) {
      console.error('Error updating repository:', error);
      throw error;
    }
  },

  /**
   * Delete a repository
   * @param {string} repositoryId - Repository ID
   * @returns {Promise<void>}
   */
  async deleteRepository(repositoryId) {
    try {
      return await authDelete(`/api/repositories/${repositoryId}`);
    } catch (error) {
      console.error('Error deleting repository:', error);
      throw error;
    }
  },

  /**
   * Sync repository data from GitHub and SonarQube
   * @param {string} repositoryId - Repository ID
   * @returns {Promise<Object>} Updated repository with synced data
   */
  async syncRepository(repositoryId) {
    try {
      return await authPost(`/api/repositories/${repositoryId}/sync`, {});
    } catch (error) {
      console.error('Error syncing repository:', error);
      throw error;
    }
  },

  /**
   * Get GitHub data for a specific repository
   * @param {string} repositoryId - Repository ID
   * @returns {Promise<Object>} GitHub data
   */
  async getGitHubData(repositoryId) {
    try {
      return await authGet(`/api/repositories/${repositoryId}/github-data`);
    } catch (error) {
      console.error('Error fetching GitHub data:', error);
      throw error;
    }
  },

  /**
   * Get SonarQube data for a specific repository
   * @param {string} repositoryId - Repository ID
   * @returns {Promise<Object>} SonarQube data
   */
  async getSonarData(repositoryId) {
    try {
      return await authGet(`/api/repositories/${repositoryId}/sonar-data`);
    } catch (error) {
      console.error('Error fetching SonarQube data:', error);
      throw error;
    }
  },

  /**
   * Bulk sync all repositories
   * @param {boolean} force - If true, sync all repositories regardless of last sync time
   * @returns {Promise<Object>} Bulk sync results
   */
  async bulkSyncRepositories(force = true) {
    try {
      const url = `/api/repositories/bulk-sync${force ? '?force=true' : ''}`;
      return await authPost(url, {});
    } catch (error) {
      console.error('Error bulk syncing repositories:', error);
      throw error;
    }
  },

  /**
   * Get Snyk vulnerability counts for all repositories
   * @returns {Promise<Object>} Map of repository ID to vulnerability counts
   */
  async getSnykVulnerabilityCounts() {
    try {
      const response = await get('/api/repositories/snyk-vulnerability-counts');
      return response.data || response;
    } catch (error) {
      console.error('Error fetching Snyk vulnerability counts:', error);
      throw error;
    }
  },

  /**
   * Get distinct streams from Snyk mapping
   * @returns {Promise<Object>} Object with streams array
   */
  async getSnykStreams() {
    try {
      return await get('/api/snyk-mapping/streams');
    } catch (error) {
      console.error('Error fetching Snyk streams:', error);
      throw error;
    }
  },

  /**
   * Get repository to stream mappings from Snyk mapping
   * @returns {Promise<Object>} Object with mappings and ignored repositories
   */
  async getRepositoryStreamMappings() {
    try {
      const response = await get('/api/snyk-mapping/repository-streams');
      return response; // Return full response with mappings and ignored
    } catch (error) {
      console.error('Error fetching repository-stream mappings:', error);
      throw error;
    }
  },

  /**
   * Get repository sync scheduler status
   * @returns {Promise<Object>} Sync scheduler status with lastRun and nextRun
   */
  async getSyncSchedulerStatus() {
    try {
      return await get('/api/repositories/sync-scheduler/status');
    } catch (error) {
      console.error('Error fetching sync scheduler status:', error);
      throw error;
    }
  }
};
